// @ts-strict-ignore
import { Item, Label, List, Popover, PopoverContent } from '@mytours/components'
import { ionHistory } from 'mobile_web/modules/navigation'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import {
	formatLanguageUrl,
	getScannerUrl,
	getSettingsUrl,
	getProfileUrl,
	getTabIndex,
	SCANNER_PAGE_SLUG,
	SETTINGS_PAGE_SLUG,
	PROFILE_PAGE_SLUG,
} from 'mobile_web/modules/navigation/routes'
import { getDirMode } from 'mobile_web/modules/platform'
import ScannerIcon from 'mobile_web/modules/scanner/components/parts/ScannerIcon'
import { getScannerLabel } from 'mobile_web/modules/scanner/functions'
import { TabConfig } from 'mytours-read-common'
import React, { useMemo, useState } from 'react'
import { tabsCount } from '../functions'
import { removeFullWidthFromTabs } from 'mobile_web/modules/map/functions'
import classNames from 'classnames'
import TabIcon from './TabIcon'
import strings from 'mobile_web/strings'
import Settings from '../../settings/functions'

// Ionic md's popover: 250px wide, kept 12px inside the viewport, its bottom 1px below the anchor.
const WIDTH = 250
const EDGE = 12

interface Props {
	keypadEnabled: boolean
	qrEnabled: boolean
	searchEnabled: boolean
	togglePopover: (event?) => void
	isSmallMobile: boolean
	isMobile: boolean
	isBigMobile: boolean
	tabs: TabConfig[]
	showScanner: boolean
	isPopoverVisible: boolean
	floorPopoverEvent: Event
}

const MorePopover: React.FC<Props> = ({
	keypadEnabled,
	qrEnabled,
	searchEnabled,
	togglePopover,
	isSmallMobile,
	isMobile,
	isBigMobile,
	tabs,
	showScanner,
	isPopoverVisible,
	floorPopoverEvent,
}) => {
	const scannerTab = useMemo(
		() =>
			showScanner && (
				<Item
					key={SCANNER_PAGE_SLUG}
					detail={false}
					className="tab-button"
					onClick={() => {
						ionHistory.push(getScannerUrl())
						removeFullWidthFromTabs()
						togglePopover()
					}}
				>
					<div className="tab-button-inner">
						<ScannerIcon
							keypadEnabled={keypadEnabled}
							qrEnabled={qrEnabled}
							searchEnabled={searchEnabled}
							className="tab-icon"
						/>
						<Label as="span">{getScannerLabel({ keypadEnabled, qrEnabled, searchEnabled })}</Label>
					</div>
				</Item>
			),
		[keypadEnabled, qrEnabled, showScanner, searchEnabled, Settings?.appLanguage?.currentLocale],
	)

	const settingsTab = useMemo(
		() => (
			<Item
				key={SETTINGS_PAGE_SLUG}
				detail={false}
				className="tab-button"
				onClick={() => {
					ionHistory.push(getSettingsUrl())
					removeFullWidthFromTabs()
					togglePopover()
				}}
			>
				<div className="tab-button-inner">
					<TabIcon tabBarIcon={'gear'} />
					<Label as="span">{strings.settings}</Label>
				</div>
			</Item>
		),
		[Settings?.appLanguage?.currentLocale],
	)

	const profileTab = useMemo(
		() => (
			<Item
				key={PROFILE_PAGE_SLUG}
				detail={false}
				className="tab-button"
				onClick={() => {
					ionHistory.push(getProfileUrl())
					removeFullWidthFromTabs()
					togglePopover()
				}}
			>
				<div className="tab-button-inner">
					<TabIcon tabBarIcon={'user'} />
					<Label as="span">{strings.profile}</Label>
				</div>
			</Item>
		),
		[Settings?.appLanguage?.currentLocale],
	)

	const tabsLength = tabsCount(tabs.length, { isSmallMobile, isMobile, isBigMobile })

	const moreTabs = useMemo(() => tabs?.slice(tabsLength) || [], [tabs, tabsLength])

	const activeIndex = NavigationService.navigationStack.getActiveTabIndex()

	// Held past close, so the leave animation stays put.
	const [anchorEvent, setAnchorEvent] = useState(floorPopoverEvent)
	if (floorPopoverEvent && floorPopoverEvent !== anchorEvent) setAnchorEvent(floorPopoverEvent)

	const anchor = useMemo(() => {
		const target = anchorEvent?.target as Element | undefined
		if (!target) return undefined
		// As wide as the popover, so centring on it left-aligns in either direction.
		const getBoundingClientRect = () => {
			const { left, top } = target.getBoundingClientRect()
			return { x: left, y: top, left, top, right: left + WIDTH, bottom: top, width: WIDTH, height: 0 } as DOMRect
		}
		const originX = getBoundingClientRect().left + WIDTH + EDGE > window.innerWidth ? 'right' : 'left'
		return { element: { getBoundingClientRect }, origin: `${originX} bottom` }
	}, [anchorEvent])

	return (
		<Popover open={isPopoverVisible} onOpenChange={open => !open && togglePopover()} modal>
			<PopoverContent
				className="floor-popover-more"
				anchor={anchor?.element}
				side="top"
				sideOffset={-1}
				collisionPadding={EDGE}
				collisionAvoidance={{ side: 'none' }}
				backdrop
				animated
				style={
					{ '--sq-popover-width': `${WIDTH}px`, '--sq-popover-transform-origin': anchor?.origin } as React.CSSProperties
				}
			>
				<List variant="ios" dir={getDirMode()} className="popover-content">
					{moreTabs.map((tab: TabConfig, index: number) => {
						const tabIndex = tabsLength + index

						if (tab.key === SCANNER_PAGE_SLUG) {
							return scannerTab
						} else if (tab.key === SETTINGS_PAGE_SLUG) {
							return settingsTab
						} else if (tab.key === PROFILE_PAGE_SLUG) {
							return profileTab
						}

						return (
							<Item
								key={index}
								onClick={() => {
									ionHistory.push(formatLanguageUrl(`/${getTabIndex(tabIndex)}`))
									NavigationService.navigationStack.setActiveTabIndex(tabIndex + 1)
									removeFullWidthFromTabs()
									togglePopover()
								}}
								className={classNames('tab-button', Number(activeIndex) === tabIndex + 1 && 'active')}
								detail={false}
								lines="none"
							>
								<div className="tab-button-inner">
									<TabIcon tabBarIcon={tab.tabBarIcon} />
									<Label as="span">{tab.tabBarTitle || '_'}</Label>
								</div>
							</Item>
						)
					})}
				</List>
			</PopoverContent>
		</Popover>
	)
}

export default MorePopover
